import { queryOptions } from "@tanstack/react-query";
import type { InvitationService } from "@/services/invitation.service";
import type { WorkspaceService } from "@/services/workspace.service";
import type { IWorkspaceMemberInvitation } from "@/types/workspace";
import { getQueryClient } from "./client";
import { FRESHNESS } from "./policies";
import { GLOBAL_TENANT, scopedKey, scopedRead, scopeIfReady, type QueryScope } from "./scope";

/**
 * The invitation reads (NG-2814):
 *
 * - `invitations.list`: a workspace's pending invitations, every page walked,
 *   as the members settings' Pending Invitations tab shows them,
 *   `[slug, uid, 'invitations', 'list']`. Mutable: it is the screen where
 *   admins invite, resend, edit and revoke.
 * - `invitations.mine`: the signed-in user's invitations to other workspaces,
 *   for the `/invitations` page, `['*', uid, 'invitations', 'mine']`. No
 *   workspace owns it, so it sits under `GLOBAL_TENANT`.
 *
 * Both walk every page with plain GETs, so both declare `"safe-read"`.
 *
 * @writers `WorkspaceMembersStore`: an invite re-reads the workspace list (the
 * answer is the new rows, not the list); resend, edit and revoke patch it from
 * their answers. `InvitationStore.acceptSelected` drops the accepted ones from
 * the user's own list.
 */

/** A workspace's invitations key. */
export function workspaceInvitationsKey(scope: QueryScope) {
  return scopedKey(scope, "invitations", "list");
}

/** The signed-in user's own invitations key; `scope` is under `GLOBAL_TENANT`. */
export function myInvitationsKey(scope: QueryScope) {
  return scopedKey(scope, "invitations", "mine");
}

/** A workspace's pending invitations. */
export function workspaceInvitationsQuery(
  service: Pick<WorkspaceService, "listInvitations">,
  scope: QueryScope,
) {
  return queryOptions({
    ...FRESHNESS.mutable,
    queryKey: workspaceInvitationsKey(scope),
    queryFn: scopedRead((request) => service.listInvitations(scope.tenant, request), {
      retry: "safe-read",
    }),
  });
}

/** The signed-in user's invitations to other workspaces. */
export function myInvitationsQuery(
  service: Pick<InvitationService, "fetchMyInvitations">,
  scope: QueryScope,
) {
  return queryOptions({
    ...FRESHNESS.mutable,
    queryKey: myInvitationsKey(scope),
    queryFn: scopedRead((request) => service.fetchMyInvitations(request), {
      retry: "safe-read",
    }),
  });
}

/**
 * Warm a workspace's invitations for a route loader. Before the session
 * resolves there is nothing to warm, and the tab's own read asks once it does.
 */
export async function prefetchWorkspaceInvitations(
  service: Pick<WorkspaceService, "listInvitations">,
  slug: string,
): Promise<void> {
  const scope = scopeIfReady(slug, "workspace invitations");
  if (!scope) return;
  await getQueryClient().fetchQuery(workspaceInvitationsQuery(service, scope));
}

/** Warm the signed-in user's invitations for the `/invitations` loader, as above. */
export async function prefetchMyInvitations(
  service: Pick<InvitationService, "fetchMyInvitations">,
): Promise<void> {
  const scope = scopeIfReady(GLOBAL_TENANT, "my invitations");
  if (!scope) return;
  await getQueryClient().fetchQuery(myInvitationsQuery(service, scope));
}

/** Invitations no one has answered yet: the ones `/invitations` offers. */
export function pendingOf(invitations: readonly IWorkspaceMemberInvitation[]) {
  return invitations.filter((invitation) => !invitation.accepted && !invitation.responded_at);
}
